import React, { useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  Text,
  VStack,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Select,
  Checkbox,
  HStack,
  Box,
  Image,
  useToast
} from '@chakra-ui/react';
import { pxToRem } from "../../../utils/editor.ts";
import { SupportService, CreateTicketRequest } from "../../../services/supportService";

interface RequestTemplateModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const RequestTemplateModal: React.FC<RequestTemplateModalProps> = ({ isOpen, onClose }) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    questionType: 'other',
    information: '',
    agreeToContact: false
  });

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = async () => {
    if (!formData.email) {
      toast({
        title: "Please fill in your email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Please enter a valid email address",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.information.trim()) {
      toast({
        title: "Please provide the template URL",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (!formData.agreeToContact) {
      toast({
        title: "Please agree to be contacted via email",
        description: "To proceed, please check the consent box.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const ticketData: CreateTicketRequest = {
        first_name: 'firstName',
        last_name: 'lastName', 
        contact_email: formData.email,
        question_type: formData.questionType,
        information: formData.information
      };

      const response = await SupportService.createTicket(ticketData);

      if (response.success) {
        toast({
          title: "Request submitted successfully!",
          status: "success",
          duration: 5000,
          isClosable: true,
        });
        onClose();
        setFormData({
            email: '',
            questionType: 'other',
            information: '',
            agreeToContact: false
        });
      } else {
        if (response.errors) {
          Object.entries(response.errors).forEach(([field, messages]) => {
            toast({
              title: `${field}: ${messages.join(', ')}`,
              status: "error",
              duration: 5000,
              isClosable: true,
            });
          });
        } else {
          toast({
            title: "Failed to create",
            description: response.message,
            status: "error",
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (error) {
      console.error('Error creating ticket:', error);
      toast({
        title: "Network Error",
        description: "Failed to submit your request. Please check your connection and try again.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
      <ModalOverlay />
      <ModalContent maxW={pxToRem(600)}>
        <ModalHeader fontFamily={'Lato'}>Request Template</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6}>
          <VStack spacing={6}>
            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Contact Email
              </FormLabel>
              <Input
                type="email"
                placeholder="Email Address"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                bg="white"
                border="1px solid"
                borderColor="#1D8AFF"
                borderRadius="6px"
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
              />
              <HStack mt={3} spacing={3} alignSelf="flex-start">
                <Checkbox
                  isChecked={formData.agreeToContact}
                  onChange={(e) => handleInputChange('agreeToContact', e.target.checked)}
                  colorScheme="blue"
                  size="md"
                  borderColor="#1D8AFF"
                  borderRadius="2px"
                />
                <Text mb={0} fontSize="sm" color="gray.700">
                  I agree to Doenba Service Team contact me via email
                </Text>
              </HStack>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Question Type
              </FormLabel>
              <Select
                value={formData.questionType}
                onChange={(e) => handleInputChange('questionType', e.target.value)}
                border="1px solid"
                bg="white"
                borderColor="#1D8AFF"
                borderRadius="6px"
                icon={<Box w={'26px'} p={'2px'}>
                  <Image
                    w={'24px'}
                    objectFit="cover"
                    src="/static/arrow-down.png"
                    alt="Doenba"
                  />
                </Box>}
              >
                <option value="other">Other</option>
              </Select>
            </FormControl>

            <FormControl isRequired>
              <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
                Template URL
              </FormLabel>
              <Textarea
                placeholder="Enter LaTex template url"
                value={formData.information}
                onChange={(e) => handleInputChange('information', e.target.value)}
                bg="white"
                border="1px solid"
                borderColor="#1D8AFF"
                borderRadius="6px"
                rows={8}
                _focus={{ borderColor: 'blue.500', boxShadow: '0 0 0 1px #3182ce' }}
              />
            </FormControl>

            <FormControl>
              <Button
                width="139px"
                h="48px"
                onClick={handleSubmit}
                isLoading={isSubmitting}
                loadingText="Submitting..."
                color="white"
                size="xl"
                bg={'#1D8AFF'}
                borderRadius="12px"
                _hover={{ bg: 'blue.600' }}
                _active={{ bg: 'blue.700' }}
                py={6}
                fontSize="md"
                fontWeight="medium"
                float={'right'}
                variant={'solid'}
                colorScheme='brand'
              >
                Submit
              </Button>
            </FormControl>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
